// Framework-agnostic replacements for `up.emit` / `up.on` / `up.event.halt`.
//
// These exist so code that has to run in both the Unpoly app and the Inertia
// app not depend on the `up` global.

type EventProps = Record<string, unknown>

export function emit(target: EventTarget, name: string, props?: EventProps): CustomEvent
export function emit(name: string, props?: EventProps): CustomEvent
export function emit(
  targetOrName: EventTarget | string,
  nameOrProps?: string | EventProps,
  maybeProps?: EventProps,
): CustomEvent {
  const [target, name, props] =
    typeof targetOrName === 'string'
      ? [document, targetOrName, nameOrProps as EventProps | undefined]
      : [targetOrName, nameOrProps as string, maybeProps]

  const event = new CustomEvent(name, { bubbles: true, cancelable: true })
  if (props) {
    Object.assign(event, props)
  }
  target.dispatchEvent(event)
  return event
}

// Like `up.on`, `names` may be a space-separated list of event names, and the return value
// unregisters every listener that was added.
export function on(
  target: EventTarget,
  names: string,
  handler: (event: any) => void,
  options?: AddEventListenerOptions,
): () => void
export function on(
  names: string,
  handler: (event: any) => void,
  options?: AddEventListenerOptions,
): () => void
export function on(
  targetOrNames: EventTarget | string,
  namesOrHandler: string | ((event: any) => void),
  handlerOrOptions?: ((event: any) => void) | AddEventListenerOptions,
  maybeOptions?: AddEventListenerOptions,
): () => void {
  const [target, names, handler, options] =
    typeof targetOrNames === 'string'
      ? [
          document,
          targetOrNames,
          namesOrHandler as (event: any) => void,
          handlerOrOptions as AddEventListenerOptions | undefined,
        ]
      : [
          targetOrNames,
          namesOrHandler as string,
          handlerOrOptions as (event: any) => void,
          maybeOptions,
        ]

  const eventNames = names.split(/\s+/).filter(Boolean)
  for (const eventName of eventNames) {
    target.addEventListener(eventName, handler, options)
  }

  return () => {
    for (const eventName of eventNames) {
      target.removeEventListener(eventName, handler, options)
    }
  }
}

// Equivalent of `up.event.halt`: stop the event from doing anything else, both for other listeners
// and for the browser's default action.
export function halt(event: Event): void {
  event.preventDefault()
  event.stopPropagation()
  event.stopImmediatePropagation()
}
